import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { productCardTemplate } from '@xshop/shared';
import { ElementNodeView, ROOT_PATH } from './ElementNode';
import { ProductScope } from './ProductScope';
import { useListProducts } from './useStoreCatalog';
import { useShopNames } from '../../commerce/shopNames';

// Sleek's "Trend this week": a row of the shop's own product cards at the end of a mega menu, three at a
// time with a 1/2 pager. They are the same cards the grids draw, so a sale, a badge or a quick view
// looks and works here exactly as it does on the page. Read only once a panel opens.

/** Which products a mega menu shows, and what it calls them. */
export type MegaProducts = { title: string; handle: string };

/** How many products the row reads, and how many it shows at once. */
const READ = 6;
const SHOWN = 3;

// Draws the titled row of product cards, a page of three at a time, or nothing while the shop has none.
export const MenuMegaProducts: React.FC<{ products: MegaProducts; id: string }> = ({ products, id }) => {
  const read = useListProducts({ kind: 'collection', handle: products.handle }, READ);
  const [page, setPage] = useState(0);
  const names = useShopNames();
  const rows = read.data ?? [];
  const pages = Math.ceil(rows.length / SHOWN);
  if (read.status !== 'ready' || rows.length === 0) return null;
  const shown = rows.slice(page * SHOWN, page * SHOWN + SHOWN);
  return (
    <section className="xe-mega-products" aria-label={products.title}>
      <div className="xe-mega-products-head">
        <h2>{products.title}</h2>
        {pages > 1 ? (
          <div className="xe-mega-pager">
            <button type="button" aria-label={names.previous} disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeft size={18} aria-hidden="true" /></button>
            <span>{page + 1}/{pages}</span>
            <button type="button" aria-label={names.next} disabled={page >= pages - 1} onClick={() => setPage(page + 1)}><ChevronRight size={18} aria-hidden="true" /></button>
          </div>
        ) : null}
      </div>
      <div className="xe-mega-products-row">
        {shown.map((row) => (
          <div key={row.id} className="xe-cat-product">
            <ProductScope product={row}>
              {productCardTemplate(`${id.replace(/[^A-Za-z0-9_-]/g, '')}-${row.id}`).map((node) => <ElementNodeView key={node.id} node={node} path={ROOT_PATH} />)}
            </ProductScope>
          </div>
        ))}
      </div>
    </section>
  );
};
